/*
 * Rebuy cart cross-sell (widget 309707, in the Rebuy Smart Cart): variant bubbles
 * --------------------------------------------------------------------------------
 * Replaces the widget's variant controls with the same radio + label markup as the product page's variant picker
 * (snippets/variant-picker.liquid / option-value.liquid), so the theme's colour swatch and block pill styles apply.
 *
 * - Rebuy renders the widget with Vue and re-renders it whenever the cart or the recommendations change. Its own
 *   controls are left in place (hidden with CSS), and a MutationObserver adds the bubbles again after each re-render.
 * - Selecting a value drives Rebuy's own (hidden) variant <select>, which is bound to product.selected_variant_id with
 *   v-model, so Rebuy's add to cart, image and price keep working unchanged.
 * - Swatch colours/images and availability come from the theme: /products/{handle}?view=rebuy-swatches
 *   (templates/product.rebuy-swatches.liquid). If that request fails, Rebuy's own controls are kept.
 * - Unavailable combinations are not shown. Like the product page, values are filtered option by option: a value is
 *   listed when an available variant has it together with the values selected in the previous options. Picking a
 *   value that doesn't exist with a later option's current value switches that later option to the first available
 *   one, so every available variant can be reached.
 *
 * Loaded before {{ content_for_header }} so its observer is registered before Rebuy's scripts run. Rebuy's widget
 * callbacks used to convert the native buttons into dropdowns (backup in docs/rebuy/); the groups in this widget are
 * flagged as already converted so that code leaves them alone.
 */
(() => {
  if (window.RebuyVariantSwatches) return;

  const WIDGET_ID = '309707';
  const WIDGET_SELECTOR = `[data-rebuy-id="${WIDGET_ID}"]`;
  const UI_CLASS = 'rebuy-theme-swatches';

  // How many values are shown before the rest collapse behind a "+N" button (only when at least 2 would be hidden).
  // Colour swatches: one row in the cart drawer (fewer on phones, where the drawer is narrower).
  const VISIBLE_VALUES = { swatch: 7, swatchSmall: 5, block: 4 };
  const smallScreen = window.matchMedia('(max-width: 480px)');

  const dataCache = new Map(); // handle -> Promise<data | null>
  const expandedOptions = new Set(); // `${productId}:${optionIndex}`
  const states = new WeakMap(); // UI element -> { select, data, productId }
  const pending = new WeakSet(); // .rebuy-product-options elements waiting for their data
  const skipped = new WeakSet(); // .rebuy-product-options elements left to Rebuy (no data, or nothing to pick)

  /* ---------------------------------------------------------------------------------------------------------------
   * Data
   * ------------------------------------------------------------------------------------------------------------- */

  function fetchProductData(handle) {
    if (!dataCache.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      const request = fetch(`${root}products/${encodeURIComponent(handle)}?view=rebuy-swatches`, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : null))
        .then((text) => {
          const data = text ? JSON.parse(text) : null;
          return data && Array.isArray(data.options) && Array.isArray(data.variants) ? data : null;
        })
        .catch(() => null);

      // Failures are kept too (until the next page load), so a broken product doesn't refetch on every DOM change
      dataCache.set(handle, request);
    }

    return dataCache.get(handle);
  }

  function getRebuyProduct(productId) {
    try {
      const widgets = window.Rebuy?.widgets;
      const list = Array.isArray(widgets) ? widgets : Object.values(widgets || {});
      const widget = list.find((item) => String(item?.data?.id) === WIDGET_ID);
      return widget?.data?.products?.find((product) => String(product.id) === productId) || null;
    } catch (error) {
      return null;
    }
  }

  function getHandle(block, productId) {
    const handle = getRebuyProduct(productId)?.handle;
    if (handle) return handle;

    const href = block.querySelector('a.rebuy-product-title-link, a.rebuy-product-image')?.getAttribute('href') || '';
    return href.match(/\/products\/([^/?#]+)/)?.[1] || null;
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Variant logic
   * ------------------------------------------------------------------------------------------------------------- */

  // Variants that are available in Shopify AND can be selected in Rebuy's select (Rebuy's copy of the product can
  // differ from Shopify's)
  function getSelectableVariants(data, select) {
    const rebuyOptions = new Map(Array.from(select.options).map((option) => [option.value, option]));

    return data.variants.filter((variant) => {
      const option = rebuyOptions.get(String(variant.id));
      return variant.available && option && !option.disabled;
    });
  }

  function matchesUpTo(variant, values, endIndex) {
    for (let index = 0; index < endIndex; index++) {
      if (variant.options[index] !== values[index]) return false;
    }
    return true;
  }

  function getVisibleValues(data, variants, selectedValues, optionIndex) {
    return data.options[optionIndex].values.filter((value) => variants.some((variant) => (
      variant.options[optionIndex] === value.name && matchesUpTo(variant, selectedValues, optionIndex)
    )));
  }

  function findVariantForChange(variants, selectedValues, optionIndex, value) {
    const wanted = selectedValues.slice();
    wanted[optionIndex] = value;

    return variants.find((variant) => variant.options.every((option, index) => option === wanted[index]))
      || variants.find((variant) => variant.options[optionIndex] === value && matchesUpTo(variant, wanted, optionIndex))
      || variants.find((variant) => variant.options[optionIndex] === value);
  }

  function selectVariant(select, variantId) {
    if (select.value === String(variantId)) return;

    select.value = String(variantId);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Rendering
   * ------------------------------------------------------------------------------------------------------------- */

  function createValue({ data, option, value, name, id, checked }) {
    const input = document.createElement('input');
    input.className = 'sr-only';
    input.type = 'radio';
    input.name = name;
    input.id = id;
    input.value = value.name;
    input.checked = checked;

    const label = document.createElement('label');
    label.htmlFor = id;

    if (option.type === 'swatch' && value.background) {
      label.className = data.swatchClass;
      if (value.white) label.classList.add('ring-inset');
      label.style.setProperty('--swatch-background', value.background);
      label.dataset.tooltip = value.name;

      const text = document.createElement('span');
      text.className = 'sr-only';
      text.textContent = value.name;
      label.appendChild(text);
    } else if (option.type === 'swatch') {
      // Values without a colour or image (e.g. "No Case (Art Only)") are text labels, like on the product page
      label.className = data.labelClass;
      label.textContent = value.name;
    } else {
      label.className = 'block-swatch';
      const text = document.createElement('span');
      text.textContent = value.name;
      label.appendChild(text);
    }

    return [input, label];
  }

  function render(ui) {
    const state = states.get(ui);
    const { data, select, productId } = state;
    const variants = getSelectableVariants(data, select);

    if (variants.length === 0) {
      // Nothing can be bought any more: give the controls back to Rebuy
      ui.remove();
      return;
    }

    let current = variants.find((variant) => String(variant.id) === select.value);

    if (!current) {
      // Rebuy's selected variant can't be bought (unavailable, or missing in Shopify): switch to the first one that can
      current = variants[0];
      selectVariant(select, current.id);
    }

    const selectedValues = current.options;
    const focusedOption = ui.contains(document.activeElement) ? document.activeElement.closest('[data-option-index]')?.dataset.optionIndex : null;
    const fragment = document.createDocumentFragment();

    data.options.forEach((option, optionIndex) => {
      const values = getVisibleValues(data, variants, selectedValues, optionIndex);
      const groupName = `${UI_CLASS}-${WIDGET_ID}-${productId}-${optionIndex}`;

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'variant-picker__option';
      fieldset.dataset.optionIndex = optionIndex;

      const legend = document.createElement('legend');
      legend.className = 'sr-only';
      legend.textContent = option.name;

      const list = document.createElement('div');
      list.className = option.type === 'swatch' && data.rectangle
        ? 'variant-picker__option-values variant-picker__option-values--color wrap gap-4'
        : 'variant-picker__option-values wrap gap-2';

      values.forEach((value, valueIndex) => {
        list.append(...createValue({
          data,
          option,
          value,
          name: groupName,
          id: `${groupName}-${valueIndex}`,
          checked: value.name === selectedValues[optionIndex]
        }));
      });

      // Collapse long lists behind a "+N" button; the selected value always stays visible
      const expandKey = `${productId}:${optionIndex}`;
      const visibleCount = option.type === 'swatch'
        ? (smallScreen.matches ? VISIBLE_VALUES.swatchSmall : VISIBLE_VALUES.swatch)
        : VISIBLE_VALUES.block;

      if (!expandedOptions.has(expandKey) && values.length > visibleCount + 1) {
        const labels = Array.from(list.querySelectorAll('label'));
        labels.forEach((label, index) => {
          label.hidden = index >= visibleCount && !label.previousElementSibling.checked;
        });

        const hiddenCount = labels.filter((label) => label.hidden).length;
        const more = document.createElement('button');
        more.type = 'button';
        more.className = `color-swatch__view-more text-xs text-subdued${data.swatchClass.includes('rounded-full') ? ' rounded-full' : ''}`;
        more.textContent = `+${hiddenCount}`;
        more.setAttribute('aria-label', `Show all ${labels.length} ${option.name} options`);
        more.addEventListener('click', () => {
          expandedOptions.add(expandKey);
          render(ui);
          ui.querySelector(`[data-option-index="${optionIndex}"] label:nth-of-type(${visibleCount + 1})`)?.previousElementSibling?.focus();
        });
        list.appendChild(more);
      }

      list.addEventListener('change', (event) => {
        const variant = findVariantForChange(variants, selectedValues, optionIndex, event.target.value);
        if (!variant) return;

        // A collapsed value picked with the arrow keys stays visible from now on
        if (event.target.nextElementSibling?.hidden) expandedOptions.add(expandKey);

        selectVariant(select, variant.id);
        render(ui);
      });

      fieldset.append(legend, list);
      fragment.appendChild(fieldset);
    });

    ui.replaceChildren(fragment);
    ui.dataset.variantId = String(current.id);

    if (focusedOption !== null && focusedOption !== undefined) {
      ui.querySelector(`[data-option-index="${focusedOption}"] input:checked`)?.focus({ preventScroll: true });
    }
  }

  async function enhance(block, optionsElement, select) {
    const productId = block.className.match(/\bproduct-id-(\d+)\b/)?.[1];
    const handle = productId && getHandle(block, productId);
    if (!handle) return;

    // While loading, Rebuy's own controls are hidden (see the CSS) so they don't flash before the bubbles appear
    pending.add(optionsElement);
    optionsElement.dataset.themeSwatches = 'loading';

    let ui = null;

    try {
      const data = await fetchProductData(handle);

      // Rebuy may have re-rendered while the data was loading
      if (!data || !optionsElement.isConnected || optionsElement.querySelector(`:scope > .${UI_CLASS}`)) return;
      if (data.variants.length < 2 || getSelectableVariants(data, select).length === 0) return;

      ui = document.createElement('div');
      ui.className = `${UI_CLASS} variant-picker`;
      states.set(ui, { data, select, productId });

      optionsElement.appendChild(ui);
      render(ui);
    } finally {
      // On any failure, Rebuy's own controls show again
      pending.delete(optionsElement);
      delete optionsElement.dataset.themeSwatches;
      if (!optionsElement.querySelector(`:scope > .${UI_CLASS}`)) skipped.add(optionsElement);
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Watching the widget
   * ------------------------------------------------------------------------------------------------------------- */

  // Rebuy's former "convert to dropdowns" callbacks skip groups flagged as converted
  function flagRebuyGroups() {
    document.querySelectorAll(`${WIDGET_SELECTOR} .rebuy-product-options__button-swatches:not([data-converted])`).forEach((group) => {
      group.dataset.converted = 'true';
    });
  }

  function scan() {
    document.querySelectorAll(`${WIDGET_SELECTOR} .rebuy-product-block .rebuy-product-options`).forEach((optionsElement) => {
      // Rebuy's own variant select (v-model="product.selected_variant_id"), not one added by other scripts
      const select = optionsElement.querySelector(':scope > select.rebuy-select[id]');
      if (!select || pending.has(optionsElement) || skipped.has(optionsElement)) return;

      const ui = optionsElement.querySelector(`:scope > .${UI_CLASS}`);

      if (!ui) {
        enhance(optionsElement.closest('.rebuy-product-block'), optionsElement, select);
        return;
      }

      const state = states.get(ui);

      if (!state) {
        ui.remove();
      } else if (state.select !== select) {
        // Rebuy replaced its select: bind to the new one
        state.select = select;
        render(ui);
      } else if (ui.dataset.variantId !== select.value) {
        // Rebuy changed the selected variant itself
        render(ui);
      }
    });
  }

  // Coalesce the mutations of one Rebuy render into one scan. A microtask (rather than requestAnimationFrame) runs
  // after Vue has finished patching but before the next paint, so Rebuy's own controls never flash, and it also runs
  // in background tabs.
  let scheduled = false;

  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;

    queueMicrotask(() => {
      scheduled = false;
      scan();
    });
  }

  function start() {
    new MutationObserver(() => {
      if (!document.querySelector(WIDGET_SELECTOR)) return;
      flagRebuyGroups();
      scheduleScan();
    }).observe(document.body, { childList: true, subtree: true });

    ['rebuy.ready', 'rebuy.productsChange', 'rebuy.selectedVariantChange', 'rebuy:cart.change', 'rebuy.show'].forEach((name) => {
      document.addEventListener(name, scheduleScan);
    });

    scheduleScan();
  }

  window.RebuyVariantSwatches = { scan: scheduleScan };

  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  }
})();
